import {
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    format,
    formatISO,
    isSameDay,
    isValid,
    parse,
    parseISO,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears,
} from "date-fns";
import type { DateRange, Matcher } from "@daypicker/react";
import { escapeRegExp } from "@helpers/escapeRegExp";
import type {
    DateOnlyRange,
    DatePickerPreset,
    DateSelectorProps,
    SingleDatePickerProps,
} from "./DatePicker.types";
import type { DatePickerCalendarOptions } from "./DatePickerCalendar";

interface WeekOptions {
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

/** Accepts both 4- and 2-digit years when parsing typed input. */
export const DEFAULT_INPUT_FORMATS: readonly string[] = [
    "MM/dd/yyyy",
    "MM/dd/yy",
];

/** Short month labels in calendar order (January → December). */
export const MONTH_LABELS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
] as const;

/** Matches the bare `yyyy-MM-dd` shape that the public API promises. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * All public values are timezone-agnostic ISO date strings (yyyy-MM-dd).
 * `Date` objects only exist internally, always at local midnight, and are
 * converted at the boundary with parseISO/formatISO so the calendar date is
 * preserved in every timezone. Partial (`2026`, `2026-07`) and full datetime
 * (`2026-07-11T02:00:00Z`) strings are rejected up front so a stored datetime
 * can never shift the calendar day by up to one day in non-UTC timezones.
 */
export const parseDateOnly = (value: string | undefined): Date | undefined => {
    if (!value || !DATE_ONLY_PATTERN.test(value)) {
        return undefined;
    }
    const parsedDate = parseISO(value);
    return isValid(parsedDate) ? parsedDate : undefined;
};

export const toDateOnly = (date: Date | undefined): string | undefined =>
    date ? formatISO(date, { representation: "date" }) : undefined;

export const toDateRange = (
    range: DateOnlyRange | undefined,
): DateRange | undefined =>
    range?.from
        ? { from: parseDateOnly(range.from), to: parseDateOnly(range.to) }
        : undefined;

export const toDateOnlyRange = (
    range: DateRange | undefined,
): DateOnlyRange | undefined =>
    range?.from
        ? { from: toDateOnly(range.from), to: toDateOnly(range.to) }
        : undefined;

export const getWeekRange = (
    date: Date,
    weekStartsOn: WeekOptions["weekStartsOn"],
): DateRange => ({
    from: startOfWeek(date, { weekStartsOn }),
    to: startOfDay(endOfWeek(date, { weekStartsOn })),
});

export const getQuarterRange = (date: Date): DateRange => ({
    from: startOfQuarter(date),
    to: startOfDay(endOfQuarter(date)),
});

export const getMonthRange = (date: Date): DateRange => ({
    from: startOfMonth(date),
    to: startOfDay(endOfMonth(date)),
});

export const getYearRange = (date: Date): DateRange => ({
    from: startOfYear(date),
    to: startOfDay(endOfYear(date)),
});

export const formatSingleDate = (
    value: string | undefined,
    displayFormat: string,
): string => {
    const date = parseDateOnly(value);
    return date ? format(date, displayFormat) : "";
};

export const formatDateRange = (
    value: DateOnlyRange | undefined,
    displayFormat: string,
): string => {
    const from = formatSingleDate(value?.from, displayFormat);

    if (!from) {
        return "";
    }

    const to = formatSingleDate(value?.to, displayFormat);
    return to && to !== from ? `${from} - ${to}` : from;
};

/** Hidden-input value for range modes; see `DatePickerValueControlProps`. */
export const serializeDateOnlyRange = (
    value: DateOnlyRange | undefined,
): string =>
    value?.from || value?.to ? `${value.from ?? ""}/${value.to ?? ""}` : "";

export const toInputFormats = (
    inputFormat: string | readonly string[],
): readonly string[] =>
    typeof inputFormat === "string" ? [inputFormat] : inputFormat;

/**
 * Builds a regex that mirrors a numeric date-fns format string (only `y`,
 * `M`, and `d` tokens are supported, which covers every input format used
 * by this picker). `y` runs must match their exact digit count so `yy` and
 * `yyyy` can coexist without ambiguity — that's the only thing that needs
 * to be strict, since a 2-digit year could otherwise be silently
 * misinterpreted as a 4-digit one (or vice versa). `M`/`d` runs accept 1 or
 * 2 digits so users can type single-digit months/days (e.g. `5/5/23`)
 * without needing to zero-pad them to match the format's own padding.
 */
const buildFormatShapePattern = (formatString: string): string => {
    let pattern = "";
    let index = 0;

    while (index < formatString.length) {
        const character = formatString[index];
        if (!character) {
            break;
        }
        if (/[a-zA-Z]/.test(character)) {
            let runLength = 0;
            while (formatString[index] === character) {
                runLength += 1;
                index += 1;
            }
            pattern += character === "y" ? `\\d{${runLength}}` : "\\d{1,2}";
        } else {
            pattern += escapeRegExp(character);
            index += 1;
        }
    }

    return pattern;
};

const matchesFormatShape = (value: string, formatString: string): boolean =>
    new RegExp(`^${buildFormatShapePattern(formatString)}$`).test(value);

/**
 * Tries each candidate format in order and accepts the first one whose
 * digit-group shape matches the value (see `matchesFormatShape`) and that
 * date-fns can parse into a valid date.
 */
export const parseSingleDate = (
    value: string,
    inputFormat: string | readonly string[],
): string | undefined => {
    const trimmedValue = value.trim();

    for (const candidateFormat of toInputFormats(inputFormat)) {
        if (!matchesFormatShape(trimmedValue, candidateFormat)) {
            continue;
        }
        const parsedDate = parse(trimmedValue, candidateFormat, new Date());
        if (isValid(parsedDate)) {
            return toDateOnly(parsedDate);
        }
    }

    return undefined;
};

export const parseDateRange = (
    value: string,
    inputFormat: string | readonly string[],
): DateOnlyRange | undefined => {
    // The separator must be surrounded by whitespace so a literal `-` inside a
    // date (e.g. an `MM-dd-yyyy` input format) is not mistaken for the range
    // separator. All current consumers use `/`-separated formats.
    const [fromValue, toValue] = value.split(/\s+[–—-]\s+/, 2);
    const from = parseSingleDate(fromValue ?? "", inputFormat);

    if (!from) {
        return undefined;
    }

    const hasEndValue = Boolean(toValue?.trim());
    const to = hasEndValue
        ? parseSingleDate(toValue ?? "", inputFormat)
        : undefined;

    if (hasEndValue && !to) {
        return undefined;
    }

    return { from, to };
};

export const areDateOnlyValuesEqual = (
    first: string | undefined,
    second: string | undefined,
): boolean => first === second;

const normalizeDateOnlyRange = (
    range: DateOnlyRange | undefined,
): DateOnlyRange | undefined => (range?.from || range?.to ? range : undefined);

export const areDateOnlyRangesEqual = (
    first: DateOnlyRange | undefined,
    second: DateOnlyRange | undefined,
): boolean => {
    const normalizedFirst = normalizeDateOnlyRange(first);
    const normalizedSecond = normalizeDateOnlyRange(second);

    return (
        normalizedFirst === normalizedSecond ||
        Boolean(
            normalizedFirst &&
            normalizedSecond &&
            normalizedFirst.from === normalizedSecond.from &&
            normalizedFirst.to === normalizedSecond.to,
        )
    );
};

const areDatesEqual = (
    first: Date | undefined,
    second: Date | undefined,
): boolean =>
    first === second || Boolean(first && second && isSameDay(first, second));

export const areRangesEqual = (
    first: DateRange | undefined,
    second: DateRange | undefined,
): boolean =>
    first === second ||
    Boolean(
        areDatesEqual(first?.from, second?.from) &&
        areDatesEqual(first?.to, second?.to),
    );

export const getDisabledMatchers = ({
    minDate,
    maxDate,
    disabledDates,
}: {
    minDate?: Date;
    maxDate?: Date;
    disabledDates?: Matcher | Matcher[];
}): Matcher[] => {
    const matchers: Matcher[] = [];

    if (minDate) {
        matchers.push({ before: minDate });
    }
    if (maxDate) {
        matchers.push({ after: maxDate });
    }
    if (Array.isArray(disabledDates)) {
        matchers.push(...disabledDates);
    } else if (disabledDates) {
        matchers.push(disabledDates);
    }

    return matchers;
};

export const getPresetId = <TValue>(preset: DatePickerPreset<TValue>): string =>
    preset.id ?? preset.label;

export const splitPresetsByPinned = <TValue>(
    presets: ReadonlyArray<DatePickerPreset<TValue>>,
    pinnedPresetIds: ReadonlySet<string>,
): {
    pinnedPresets: ReadonlyArray<DatePickerPreset<TValue>>;
    unpinnedPresets: ReadonlyArray<DatePickerPreset<TValue>>;
} => {
    const pinnedPresets: Array<DatePickerPreset<TValue>> = [];
    const unpinnedPresets: Array<DatePickerPreset<TValue>> = [];
    presets.forEach((preset) => {
        if (pinnedPresetIds.has(getPresetId(preset))) {
            pinnedPresets.push(preset);
        } else {
            unpinnedPresets.push(preset);
        }
    });
    return { pinnedPresets, unpinnedPresets };
};

export const getDefaultSinglePresets = (
    today: Date,
): ReadonlyArray<DatePickerPreset<string>> => [
    { label: "Today", value: toDateOnly(today) ?? "" },
    { label: "Yesterday", value: toDateOnly(subDays(today, 1)) ?? "" },
];

const toPresetRange = (range: DateRange): DateOnlyRange =>
    toDateOnlyRange(range) ?? {};

export const getDefaultRangePresets = (
    today: Date,
    weekStartsOn: WeekOptions["weekStartsOn"],
): ReadonlyArray<DatePickerPreset<DateOnlyRange>> => {
    const previousWeek = subWeeks(today, 1);
    const previousMonth = subMonths(today, 1);
    const previousYear = subYears(today, 1);

    return [
        { label: "Today", value: toPresetRange({ from: today, to: today }) },
        {
            label: "This week",
            value: toPresetRange(getWeekRange(today, weekStartsOn)),
        },
        {
            label: "Last week",
            value: toPresetRange(getWeekRange(previousWeek, weekStartsOn)),
        },
        {
            label: "This month",
            value: toPresetRange(getMonthRange(today)),
        },
        {
            label: "Last month",
            value: toPresetRange(getMonthRange(previousMonth)),
        },
        {
            label: "This year",
            value: toPresetRange(getYearRange(today)),
        },
        {
            label: "Last year",
            value: toPresetRange(getYearRange(previousYear)),
        },
    ];
};

/**
 * Options every date calendar shares. A clearable single-day picker is
 * required: clicking the selected day again keeps it, so only Clear empties
 * the value.
 */
export const getCalendarOptions = (
    props: Pick<
        SingleDatePickerProps,
        | "calendarClassName"
        | "required"
        | "clearable"
        | "showOutsideDays"
        | "showWeekNumbers"
        | "fixedWeeks"
        | "numberOfMonths"
        | "isPopoverSizeFixed"
        | "minDate"
        | "maxDate"
        | "disabledDates"
    > & { mode: DateSelectorProps["mode"] },
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6,
): DatePickerCalendarOptions => ({
    className: props.calendarClassName,
    required:
        props.mode === "day"
            ? props.required === true || props.clearable !== false
            : props.required,
    showOutsideDays: props.showOutsideDays,
    showWeekNumbers: props.showWeekNumbers,
    fixedWeeks: props.fixedWeeks,
    numberOfMonths: props.numberOfMonths,
    isPopoverSizeFixed: props.isPopoverSizeFixed,
    minDate: parseDateOnly(props.minDate),
    maxDate: parseDateOnly(props.maxDate),
    disabledDates: props.disabledDates,
    weekStartsOn,
});
